if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      variantRequestSequence = 0;
      variantRequestOptions = null;
      variantRecoveryController = null;
      quantityRulesRequestSequence = 0;
      quantityRulesController = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.initializeVariantRecovery();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initializeAcrylicQuantityGuard();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.variantRecoveryController?.abort();
        this.acrylicQuantityController?.abort();
        this.acrylicQuantityController = null;
        this.abortController?.abort();
        this.variantRequestSequence += 1;
        this.cancelQuantityRulesRequest();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      initializeVariantRecovery() {
        this.variantRecoveryController?.abort();
        this.variantRecoveryController = new AbortController();
        const options = { capture: true, signal: this.variantRecoveryController.signal };
        this.addEventListener('click', (event) => {
          const retry = event.target.closest('[data-idw-variant-retry]');
          if (retry && this.contains(retry)) {
            event.preventDefault();
            if (this.dataset.idwVariantState === 'error' && this.variantRequestOptions) {
              this.resetProductFormState();
              this.renderProductInfo(this.variantRequestOptions);
            }
            return;
          }
          this.blockPendingVariantPurchase(event);
        }, options);
        this.addEventListener('submit', this.blockPendingVariantPurchase.bind(this), options);
      }

      blockPendingVariantPurchase(event) {
        if (!['loading', 'error'].includes(this.dataset.idwVariantState)) return;
        const target = event.target;
        const purchase = event.type === 'submit'
          ? target.matches('form[action*="/cart/add"]')
          : target.closest('product-form [type="submit"], .gpo-clone-atc-button, .gpo-clone-stickyAtc-button, .shopify-payment-button');
        if (!purchase) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.dataset.idwVariantState === 'error') this.querySelector('[data-idw-variant-status]')?.focus();
      }

      setVariantRequestState(state) {
        this.dataset.idwVariantState = state;
        const optionsPending = ['loading', 'error'].includes(this.productForm?.dataset?.idwOptionsState);
        this.querySelectorAll('[data-idw-dynamic-checkout]').forEach((element) => {
          element.hidden = state !== 'ready' || optionsPending;
          element.inert = element.hidden;
        });
        const status = this.querySelector('[data-idw-variant-status]');
        if (status) {
          status.hidden = state === 'ready';
          status.setAttribute('aria-busy', String(state === 'loading'));
          const text = status.querySelector('[data-idw-variant-message]');
          if (text) text.textContent = state === 'error' ? status.dataset.errorText : status.dataset.loadingText;
          const retry = status.querySelector('[data-idw-variant-retry]');
          if (retry) retry.hidden = state !== 'error';
        }
        this.dispatchEvent(new CustomEvent('idw:variant-request-state', { bubbles: true, detail: { state } }));
      }

      async renderProductInfo({ requestUrl, targetId, callback }) {
        const sequence = ++this.variantRequestSequence;
        this.cancelQuantityRulesRequest();
        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;
        this.variantRequestOptions = { requestUrl, targetId, callback };
        this.setVariantRequestState('loading');
        let timedOut = false;
        const timer = setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, 20000);
        try {
          const response = await fetch(requestUrl, { signal: controller.signal });
          if (!response.ok) throw new Error('Product options request failed: ' + response.status);
          const responseText = await response.text();
          if (sequence !== this.variantRequestSequence || !this.isConnected) return;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceProduct = html.querySelector('product-info');
          const sourceVariant = sourceProduct?.querySelector('form[action*="/cart/add"] input[name="id"]');
          if (!sourceProduct || (!sourceProduct.querySelector('variant-selects') && !sourceVariant?.value)) {
            throw new Error('Product options response is incomplete');
          }
          this.pendingRequestUrl = null;
          callback(html);
          if (sequence !== this.variantRequestSequence || !this.isConnected) return;
          this.setVariantRequestState('ready');
          document.getElementById(targetId)?.focus();
        } catch (error) {
          if (sequence !== this.variantRequestSequence || !this.isConnected) return;
          if (error.name === 'AbortError' && !timedOut) return;
          this.setVariantRequestState('error');
          this.productForm?.toggleSubmitButton(true);
          this.variantSelectors?.rejectPendingSelectPromise(error);
          this.querySelector('[data-idw-variant-status]')?.focus();
          console.error(error);
        } finally {
          clearTimeout(timer);
        }
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html, { preserveQuantity: this.isAcrylicQuantityPilot() && productUrl === this.dataset.url });
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          const sourceSubmitButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const sourceDisabled = sourceSubmitButton?.hasAttribute('data-idw-native-disabled')
            ? sourceSubmitButton.getAttribute('data-idw-native-disabled') === 'true'
            : sourceSubmitButton?.hasAttribute('disabled') ?? true;
          this.productForm?.toggleSubmitButton(sourceDisabled, window.variantStrings.soldOut);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.cancelQuantityRulesRequest();
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      cancelQuantityRulesRequest() {
        this.quantityRulesRequestSequence += 1;
        this.quantityRulesController?.abort();
        this.quantityRulesController = null;
        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden');
      }

      async fetchQuantityRules() {
        this.cancelQuantityRulesRequest();
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId || !this.isConnected || ['loading', 'error'].includes(this.dataset.idwVariantState)) return;

        const sequence = this.quantityRulesRequestSequence;
        const variantSequence = this.variantRequestSequence;
        const productUrl = this.dataset.url;
        const sectionId = this.dataset.section;
        const controller = new AbortController();
        this.quantityRulesController = controller;
        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        const isCurrentRequest = () => this.isConnected && !controller.signal.aborted &&
          sequence === this.quantityRulesRequestSequence && variantSequence === this.variantRequestSequence &&
          currentVariantId === this.productForm?.variantIdInput?.value &&
          productUrl === this.dataset.url && sectionId === this.dataset.section &&
          !['loading', 'error'].includes(this.dataset.idwVariantState);

        spinner?.classList.remove('hidden');
        try {
          const response = await fetch(`${productUrl}?variant=${currentVariantId}&section_id=${sectionId}`, { signal: controller.signal });
          if (!response.ok) throw new Error('Quantity rules request failed: ' + response.status);
          const responseText = await response.text();
          // A cancelled fetch can still finish reading its body. Only its current owner may update the form.
          if (!isCurrentRequest()) return;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          if (!html.getElementById(`Quantity-Form-${sectionId}`)?.querySelector('.quantity__input')) {
            throw new Error('Quantity rules response is incomplete');
          }
          this.updateQuantityRules(sectionId, html);
        } catch (error) {
          if (isCurrentRequest() && error.name !== 'AbortError') console.error(error);
        } finally {
          if (sequence === this.quantityRulesRequestSequence) {
            spinner?.classList.add('hidden');
            this.quantityRulesController = null;
          }
        }
      }

      updateQuantityRules(sectionId, html, options = {}) {
        if (!this.quantityInput) return;
        const preserveQuantity = options.preserveQuantity && this.isAcrylicQuantityPilot();
        if (!preserveQuantity) this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
        if (preserveQuantity) this.applyAcrylicQuantityRules();
        else if (this.isAcrylicQuantityPilot()) this.validateAcrylicQuantity();
      }

      isAcrylicQuantityPilot() {
        if (!this.id?.startsWith('MainProduct-') || this.dataset.originalSection) return false;
        if (this.closest('quick-add-modal, featured-product')) return false;
        try { return ['/products/acrylic-stand', '/products/wagging-tail-acrylic-keychain', '/products/lying-down-plush'].includes(new URL(this.dataset.url, window.location.origin).pathname); }
        catch (_) { return false; }
      }

      initializeAcrylicQuantityGuard() {
        if (!this.isAcrylicQuantityPilot() || !this.quantityInput || !this.quantityForm) return;
        this.acrylicQuantityController?.abort();
        this.acrylicQuantityController = new AbortController();
        const options = { capture: true, signal: this.acrylicQuantityController.signal };
        this.setAttribute('data-idw-acrylic-quantity-guard', '');
        if (!this.querySelector('[data-idw-acrylic-quantity-style]')) {
          const style = document.createElement('style');
          style.setAttribute('data-idw-acrylic-quantity-style', '');
          // A dedicated CSS gate cannot be undone by another owner's hidden/inert
          // refresh. Clearing our error never releases another owner's button lock.
          style.textContent = 'product-info[data-idw-acrylic-quantity-guard][data-idw-quantity-invalid] [data-idw-dynamic-checkout], product-info[data-idw-acrylic-quantity-guard][data-idw-quantity-invalid] .shopify-payment-button, product-info[data-idw-acrylic-quantity-guard][data-idw-quantity-invalid] shopify-accelerated-checkout { display:none!important; pointer-events:none!important; }';
          this.append(style);
        }
        this.addEventListener('input', event => { if (event.target === this.quantityInput) this.validateAcrylicQuantity(); }, options);
        this.addEventListener('change', event => { if (event.target === this.quantityInput) this.validateAcrylicQuantity(); }, options);
        document.addEventListener('submit', this.blockAcrylicQuantityPurchase.bind(this), options);
        document.addEventListener('click', this.blockAcrylicQuantityPurchase.bind(this), options);
        document.addEventListener('keydown', event => {
          if (event.key === 'Enter' || event.key === ' ') this.blockAcrylicQuantityPurchase(event);
        }, options);
        this.validateAcrylicQuantity();
      }

      readAcrylicQuantityRules() {
        const input = this.quantityInput;
        const minText = input.getAttribute('data-min');
        const maxText = input.getAttribute('data-max');
        const stepText = input.getAttribute('step');
        const cartText = input.getAttribute('data-cart-quantity');
        const integer = value => typeof value === 'string' && /^\d+$/.test(value) && Number.isSafeInteger(Number(value));
        if (!integer(minText) || Number(minText) < 1 || !integer(stepText) || Number(stepText) < 1 ||
            !integer(cartText) || (maxText !== null && !integer(maxText))) return { error: '数量の条件を確認できません。ページを再読み込みしてください。' };
        const baseMin = Number(minText), step = Number(stepText), cart = Number(cartText);
        const max = maxText === null ? null : Number(maxText) - cart;
        if (max !== null && max <= 0) return { min: baseMin, max: 0, step, error: '選択した仕様は、現在追加で注文できる数量がありません。カート内の数量もご確認ください。' };
        if (maxText !== null && Number(maxText) < baseMin) return { error: '数量の条件を確認できません。ページを再読み込みしてください。' };
        let min = max === null ? baseMin : Math.min(baseMin, max);
        if (cart >= baseMin) min = Math.min(min, step);
        return { min, max, step };
      }

      applyAcrylicQuantityRules() {
        const rules = this.readAcrylicQuantityRules();
        if (rules.min !== undefined) this.quantityInput.min = String(rules.min);
        if (rules.max === null) this.quantityInput.removeAttribute('max');
        else if (rules.max !== undefined) this.quantityInput.max = String(rules.max);
        // Do not write input.value: it may have changed while the variant request
        // was pending. The customer's latest input survives valid and invalid cases.
        this.validateAcrylicQuantity(rules);
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      validateAcrylicQuantity(rules = this.readAcrylicQuantityRules()) {
        if (!this.isAcrylicQuantityPilot() || !this.quantityInput) return true;
        const value = this.quantityInput.value;
        const count = Number(value);
        let error = rules.error || '';
        if (!error && (!/^\d+$/.test(value) || !Number.isSafeInteger(count) || count < 1)) error = '数量は1以上の整数で入力してください。';
        if (!error && (count < rules.min || (rules.max !== null && count > rules.max) || (count - rules.min) % rules.step !== 0)) {
          const limits = [rules.min + '個以上', rules.max === null ? '' : rules.max + '個以下', rules.step > 1 ? rules.step + '個単位' : ''].filter(Boolean).join('・');
          error = '選択した仕様で注文できる数量をご確認ください。（' + limits + '）';
        }
        this.showAcrylicQuantityError(error);
        return !error;
      }

      showAcrylicQuantityError(message) {
        let node = this.querySelector('[data-idw-acrylic-quantity-message]');
        if (!node && message) {
          node = document.createElement('p');
          node.setAttribute('data-idw-acrylic-quantity-message', '');
          node.id = 'IdwAcrylicQuantityError-' + this.dataset.section;
          node.className = 'product-form__error-message';
          node.setAttribute('role', 'status');
          node.setAttribute('aria-live', 'polite');
          node.style.cssText = 'margin:8px 0;font-size:13px;line-height:1.5;color:#b42318;scroll-margin-top:calc(var(--header-height,68px) + 16px);';
          this.quantityForm.append(node);
        }
        if (node) {
          if (node.textContent !== message) node.textContent = message;
          node.hidden = !message;
          const describedBy = (this.quantityInput.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean).filter(id => id !== node.id);
          if (message) describedBy.push(node.id);
          if (describedBy.length) this.quantityInput.setAttribute('aria-describedby', describedBy.join(' '));
          else this.quantityInput.removeAttribute('aria-describedby');
        }
        this.toggleAttribute('data-idw-quantity-invalid', Boolean(message));
        // Do not setCustomValidity, clear a plugin error, or change its disabled,
        // hidden or inert state. The capture guard below owns only this error.
      }

      blockAcrylicQuantityPurchase(event) {
        if (!this.isAcrylicQuantityPilot() || !this.isConnected) return;
        const form = this.productForm?.querySelector('form[action*="/cart/add"]');
        if (!form) return;
        let purchase;
        if (event.type === 'submit') purchase = event.target === form;
        else {
          const control = event.target.closest?.('[type="submit"], [name="add"], .gpo-clone-atc-button, .gpo-clone-stickyAtc-button, [data-idw-dynamic-checkout], .shopify-payment-button, shopify-accelerated-checkout');
          if (!control) return;
          const owner = control.closest('product-info');
          const explicitForm = control.form || (control.getAttribute('form') && document.getElementById(control.getAttribute('form')));
          const gpo = window.GPOConfigs;
          const externalClone = !owner && !explicitForm && control.matches('.gpo-clone-atc-button, .gpo-clone-stickyAtc-button') && gpo?.form === form && String(gpo.product?.id) === this.dataset.productId;
          purchase = explicitForm ? explicitForm === form : owner === this || externalClone;
        }
        if (!purchase || this.validateAcrylicQuantity()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.quantityInput.focus({ preventScroll: true });
        this.querySelector('[data-idw-acrylic-quantity-message]')?.scrollIntoView({ block: 'center', behavior: 'auto' });
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

